import { useLoaderData, useRevalidator, useRouteError } from "react-router-dom";
import GenericErrorDisplay, { useSetError } from "../generic-error";
import LcService from "../../service/lc";
import { PublicLc } from "../../dtos/lc";
import NumberInput from "../../components/inputs/number";
import TransmedReceivingService from "../../service/transmed/process/receiving";
import TransmedReceiving from "../../dtos/transmed/process/receiving";
import dayjs, { Dayjs } from "dayjs";
import { useState } from "react";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { CircularProgress } from "@mui/material";
import TextInput from "../../components/inputs/text";
import DateRangeInput from "../../components/inputs/date/range";
import TransmedProcessService from "../../service/transmed/process";
import SwitchInput from "../../components/inputs/checkbox/switch";
import { default as Markdown } from "react-remarkable";
import TransmedEdition from "../../dtos/transmed/process/edition";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfinity } from "@fortawesome/free-solid-svg-icons";

export const path = "transmed/receivings";

export async function loader() {
  const lcs = new LcService().read.all();
  const receivings = new TransmedReceivingService().read.all();
  const edition = new TransmedProcessService().read.edition();
  return [await lcs, await receivings, await edition];
}

function ReceivingOrLC({
  entry,
  processStarted = false,
}: {
  entry: PublicLc | TransmedReceiving;
  processStarted?: boolean;
}) {
  const service = new TransmedReceivingService();
  const revalidator = useRevalidator();
  const setErrorOnly = useSetError();
  const setError = (error: unknown) => {
    setErrorOnly(error);
    revalidator.revalidate();
  };

  const lc = entry instanceof PublicLc ? entry : entry.lc;

  const [accomodationYes, setAccomodationYes] = useState<number | null>(
    entry instanceof TransmedReceiving ? entry.accomodation_yes : null,
  );
  const [accomodationNo, setAccomodationNo] = useState<number | null>(
    entry instanceof TransmedReceiving ? entry.accomodation_no : null,
  );
  const [exchangeInterval, setExchangeInterval] = useState<DateRange<Dayjs>>(
    entry instanceof TransmedReceiving
      ? [dayjs(entry.exchange_start), dayjs(entry.exchange_end)]
      : [null, null],
  );
  const [whatsapp, setWhatsapp] = useState<string>(
    entry instanceof TransmedReceiving ? entry.whatsapp : "",
  );
  const [acceptanceMessage, setAcceptanceMessage] = useState<string>(
    entry instanceof TransmedReceiving ? entry.acceptance_message : "",
  );
  const [payment, setPayment] = useState<boolean>(
    entry instanceof TransmedReceiving ? entry.payment : true,
  );

  const [enabled, setEnabled] = useState<boolean>(
    entry instanceof TransmedReceiving,
  );
  const [loading, setLoading] = useState<boolean>(false);

  return (
    <form
      className="surface"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "flex-start",
      }}
      onSubmit={async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
          if (exchangeInterval[0] === null)
            throw new TypeError("Exchange period start cannot be empty");
          if (exchangeInterval[1] === null)
            throw new TypeError("Exchange period end cannot be empty");

          if (entry instanceof TransmedReceiving)
            await service.update.receiving(
              entry.id,
              entry.lc.id,
              accomodationYes,
              accomodationNo,
              exchangeInterval[0].toDate(),
              exchangeInterval[1].toDate(),
              whatsapp,
              acceptanceMessage,
              payment,
            );
          else
            await service.create.receiving(
              entry.id,
              accomodationYes,
              accomodationNo,
              exchangeInterval[0].toDate(),
              exchangeInterval[1].toDate(),
              whatsapp,
              acceptanceMessage,
              payment,
            );
          revalidator.revalidate();
        } catch (error) {
          setError(error);
        } finally {
          setLoading(false);
        }
      }}
    >
      <span
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: "var(--gap-small)",
          marginBottom: "var(--gap-medium)",
        }}
      >
        <h3>{lc.name}</h3>
        {(!enabled || entry instanceof PublicLc) && <i>(fără locuri)</i>}
      </span>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "var(--gap-small) 40px",
        }}
      >
        <div
          style={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "row",
            gap: "var(--gap-small)",
          }}
        >
          <NumberInput
            style={{ flexGrow: 1 }}
            label={
              "Locuri cu cazare" +
              (accomodationYes === null ? " nelimitate" : "")
            }
            value={accomodationYes ?? ""}
            onChange={(e) =>
              setAccomodationYes(
                isNaN(parseInt(e.target.value))
                  ? null
                  : parseInt(e.target.value),
              )
            }
            disabled={processStarted || !enabled || accomodationYes === null}
            required={accomodationYes !== null}
          />
          <SwitchInput
            disabled={processStarted || !enabled}
            checked={accomodationYes === null && enabled}
            onChange={(e) => setAccomodationYes(e.target.checked ? null : 0)}
            label={<FontAwesomeIcon icon={faInfinity} />}
          />
        </div>
        <div
          style={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "row",
            gap: "var(--gap-small)",
          }}
        >
          <NumberInput
            style={{ flexGrow: 1 }}
            label={
              "Locuri fără cazare" +
              (accomodationNo === null ? " nelimitate" : "")
            }
            value={accomodationNo ?? ""}
            onChange={(e) =>
              setAccomodationNo(
                isNaN(parseInt(e.target.value))
                  ? null
                  : parseInt(e.target.value),
              )
            }
            disabled={processStarted || !enabled || accomodationNo === null}
            required={accomodationNo !== null}
          />
          <SwitchInput
            disabled={processStarted || !enabled}
            checked={accomodationNo === null && enabled}
            onChange={(e) => setAccomodationNo(e.target.checked ? null : 0)}
            label={<FontAwesomeIcon icon={faInfinity} />}
          />
        </div>
      </div>
      <DateRangeInput
        className="responsive-range-input"
        labelStart="Început schimb"
        labelEnd="Sfârșit schimb"
        value={exchangeInterval}
        onChange={(value) => setExchangeInterval(value)}
        disabled={processStarted || !enabled}
      />
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexWrap: "wrap",
          gap: 40,
          marginTop: "var(--gap-big)",
        }}
      >
        <div
          style={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "flex-start",
          }}
        >
          <TextInput
            label="Link grup incomings"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            disabled={!enabled}
            required
          />
          <TextInput
            label="Mesaj de primire"
            value={acceptanceMessage}
            onChange={(e) => setAcceptanceMessage(e.target.value)}
            disabled={!enabled}
            style={{ marginTop: "var(--gap-small)" }}
            helperText="Permite formatare Markdown"
            required
            multiline
            rows={6}
          />
          <SwitchInput
            label="Necesită încărcarea dovezii plății la acceptare"
            checked={payment}
            onChange={(e) => setPayment(e.target.checked)}
            disabled={!enabled}
          />
          <div
            style={{
              marginTop: "var(--gap-small)",
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "var(--gap-small)",
            }}
          >
            <button
              type="submit"
              className="primary button"
              disabled={!enabled}
            >
              Salvează
            </button>
            <button
              type="button"
              className={"button " + (enabled ? "secondary" : "primary")}
              disabled={processStarted}
              onClick={async () => {
                if (enabled && entry instanceof TransmedReceiving) {
                  setLoading(true);
                  setError("");
                  try {
                    await service.delete.coordinator(entry.id);
                  } catch (error) {
                    setError(error);
                  } finally {
                    revalidator.revalidate();
                    setLoading(false);
                  }
                } else {
                  setAccomodationYes(0);
                  setAccomodationNo(0);
                }

                setEnabled((e) => !e);
              }}
            >
              {enabled ? "Dezactivează" : "Activează"}
            </button>
            {(loading || revalidator.state === "loading") && (
              <CircularProgress />
            )}
            {(entry instanceof TransmedReceiving
              ? entry.accomodation_yes !== accomodationYes ||
                entry.accomodation_no !== accomodationNo ||
                dayjs(entry.exchange_start).toISOString() !==
                  exchangeInterval[0]?.toISOString() ||
                dayjs(entry.exchange_end).toISOString() !==
                  exchangeInterval[1]?.toISOString()
              : enabled) && <i> (nesalvat)</i>}
          </div>
        </div>
        <div
          style={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            justifyContent: "flex-start",
            gap: "var(--gap-small)",
          }}
        >
          <h4>Mesaj de primire formatat</h4>
          <div className="surface" style={{ flex: 1 }}>
            <Markdown source={acceptanceMessage} />
          </div>
        </div>
      </div>
    </form>
  );
}

export function Component() {
  const [lcs, receivings, edition] = useLoaderData() as [
    PublicLc[],
    TransmedReceiving[],
    TransmedEdition,
  ];

  return (
    <div
      id="page-account-transmed-receivings"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "flex-start",
        gap: "var(--gap-medium)",
      }}
    >
      <h1>Locuri transmed</h1>
      {edition.participation_proof_deadline < new Date() && (
        <div style={{ color: "red" }}>
          <strong>Atenție!</strong> În acest moment editezi locurile ediției
          care s-a terminat.
        </div>
      )}
      {[
        ...receivings,
        ...lcs.filter(
          (lc) =>
            lc.transmed &&
            receivings.find((receiving) => receiving.lc.id === lc.id) ===
              undefined,
        ),
      ]
        .sort((a, b) => {
          const vala = a instanceof TransmedReceiving ? a.lc.id : a.id;
          const valb = b instanceof TransmedReceiving ? b.lc.id : b.id;

          return vala === valb ? 0 : vala < valb ? -1 : 1;
        })
        .map((entry) => (
          <ReceivingOrLC
            key={entry instanceof TransmedReceiving ? entry.lc.id : entry.id}
            entry={entry}
            processStarted={edition.process_started}
          />
        ))}
    </div>
  );
}
Component.displayName = "page-account-transmed-receivings";

export function ErrorBoundary() {
  const error = useRouteError();
  return <GenericErrorDisplay error={error} />;
}
ErrorBoundary.displayName = "page-account-transmed-receivings-error";
